import { formatCompact, formatMoney } from '@/utils/format';
import { toKesEquivalent } from '@/utils/invoice';
import { agency } from '@/mocks/portal';
import { useAuth } from '@/context/AuthContext';
import { useBrand } from '@/context/BrandContext';
import type {
  CampaignReportRow,
  CreatorPerformanceRow,
} from '@/types/portal';

interface ReportPrintViewProps {
  sections: string[];
  periodLabel: string;
  market: string;
  generatedAt: string;
  campaignRows: CampaignReportRow[];
  creatorRows: CreatorPerformanceRow[];
}

const thClass =
  'border-b border-background-300 px-2 py-1.5 text-left text-[10px] font-semibold uppercase tracking-wider text-foreground-500';
const tdClass = 'border-b border-background-200 px-2 py-1.5 text-[11px] text-foreground-800';

export default function ReportPrintView({
  sections,
  periodLabel,
  market,
  generatedAt,
  campaignRows,
  creatorRows,
}: ReportPrintViewProps) {
  const { profile, logos } = useBrand();
  const { user } = useAuth();
  const brandName = profile.name || agency.name;
  const brandMark = logos.mark || logos.avatar;

  return (
    <div className="print-only bg-white p-8 text-foreground-950">
      <header className="mb-6 flex items-start justify-between border-b border-background-300 pb-4">
        <div>
          <div className="flex items-center gap-3">
            <span
              className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-md border border-background-300 text-sm font-bold"
              style={brandMark ? undefined : { backgroundColor: '#1F1B17', color: '#FFFFFF' }}
            >
              {brandMark ? (
                <img
                  src={brandMark}
                  alt={`${brandName} logo`}
                  className="h-full w-full object-contain"
                />
              ) : (
                profile.logoInitials || '··'
              )}
            </span>
            <div>
              <p className="font-heading text-base font-semibold text-foreground-950">
                {brandName}
              </p>
              <p className="text-[10px] uppercase tracking-wider text-foreground-500">
                powered by {agency.poweredBy}
              </p>
            </div>
          </div>
          <h1 className="mt-3 font-heading text-xl font-semibold text-foreground-950">
            Portfolio performance report
          </h1>
          <p className="mt-1 text-[11px] text-foreground-600">
            {periodLabel} · {market} · Generated {generatedAt}
          </p>
          <p className="mt-1 text-[11px] text-foreground-600">
            Report owner ·{' '}
            <span className="font-medium text-foreground-800">{user?.name}</span> ·{' '}
            {user?.roleName}
          </p>
        </div>
        <div className="text-right">
          <p className="text-[11px] text-foreground-500">Base currency</p>
          <p className="text-sm font-medium text-foreground-900">KES equivalent</p>
        </div>
      </header>

      {sections.includes('campaign') ? (
        <section className="mb-7">
          <h2 className="mb-2 font-heading text-sm font-semibold text-foreground-950">
            Campaign performance
          </h2>
          <table className="w-full border-collapse">
            <thead>
              <tr>
                <th className={thClass}>Campaign</th>
                <th className={thClass}>Client</th>
                <th className={thClass}>Market</th>
                <th className={thClass}>Budget</th>
                <th className={thClass}>Spend</th>
                <th className={thClass}>Reach</th>
                <th className={thClass}>Eng. rate</th>
                <th className={thClass}>Status</th>
              </tr>
            </thead>
            <tbody>
              {campaignRows.map((row) => (
                <tr key={row.id}>
                  <td className={tdClass}>{row.name}</td>
                  <td className={tdClass}>{row.brandClient}</td>
                  <td className={tdClass}>{row.market}</td>
                  <td className={tdClass}>{formatMoney(row.budget, row.currency)}</td>
                  <td className={tdClass}>{formatMoney(row.spend, row.currency)}</td>
                  <td className={tdClass}>{formatCompact(row.reach)}</td>
                  <td className={tdClass}>{row.engagementRate.toFixed(1)}%</td>
                  <td className={tdClass}>{row.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      ) : null}

      {sections.includes('creator') ? (
        <section>
          <h2 className="mb-2 font-heading text-sm font-semibold text-foreground-950">
            Creator performance
          </h2>
          <table className="w-full border-collapse">
            <thead>
              <tr>
                <th className={thClass}>Creator</th>
                <th className={thClass}>Campaign</th>
                <th className={thClass}>Tier</th>
                <th className={thClass}>Reach</th>
                <th className={thClass}>Eng. rate</th>
                <th className={thClass}>On-time</th>
                <th className={thClass}>Paid</th>
                <th className={thClass}>Rating</th>
              </tr>
            </thead>
            <tbody>
              {creatorRows.map((row) => (
                <tr key={row.id}>
                  <td className={tdClass}>{row.name}</td>
                  <td className={tdClass}>{row.campaign}</td>
                  <td className={tdClass}>{row.tier}</td>
                  <td className={tdClass}>{formatCompact(row.reach)}</td>
                  <td className={tdClass}>{row.engagementRate.toFixed(1)}%</td>
                  <td className={tdClass}>{row.onTimeRate}%</td>
                  <td className={tdClass}>
                    {formatMoney(row.paid, row.currency)}
                    {row.currency !== 'KES'
                      ? ` (${formatMoney(toKesEquivalent(row.paid, row.currency), 'KES')})`
                      : ''}
                  </td>
                  <td className={tdClass}>{row.rating.toFixed(1)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      ) : null}

      <footer className="mt-8 border-t border-background-300 pt-3 text-[10px] text-foreground-500">
        Confidential — prepared by {brandName}. Figures are demonstration data for the CPaaS
        agency portal.
      </footer>
    </div>
  );
}